// window.atlas.appConfig.* — read the app's config at runtime (App Config feature).
//
// An app ships a config.json in its bundle (declared in package.json `atlas.config`); owners edit it
// from the Console without a redeploy. Config is PRIVATE: it is no longer served as a public
// /<app>/config.json static asset. This namespace reads the values via the authed control-plane API
// (GET /apps/<app>/config/values), so config is readable only by signed-in Atlas users — the SDK
// attaches the Cognito token and handles 401 re-login. (Still visible in that user's browser, so it
// is NOT for secrets — broker those server-side.) Named `appConfig` because `window.atlas.config` is
// already the SDK's own resolved client config.
//
// Note: `window.atlas.config` is the SDK's *own* settings; this is the *app's* settings.

import type { ApiRequester } from "./api";
import type { AppConfigApi } from "./types";

export function makeAppConfig(api: ApiRequester, currentAppName: string): AppConfigApi {
  let cache: Record<string, unknown> | undefined; // resolved config object, fetched once per page load

  async function getAll(): Promise<Record<string, unknown>> {
    if (cache !== undefined) return cache;
    let resolved: Record<string, unknown> = {};
    if (currentAppName) {
      try {
        const res = await api.request("GET", `/apps/${encodeURIComponent(currentAppName)}/config/values`);
        const parsed = res && res.config;
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) resolved = parsed;
      } catch {
        // No config (or a network/auth error) → empty config; callers use their fallback.
      }
    }
    cache = resolved;
    return resolved;
  }

  return {
    // Resolve the whole config object (cached for the page).
    getAll,
    // Resolve one key, returning `fallback` if absent.
    async get(key: string, fallback: unknown = undefined) {
      const all = await getAll();
      return key in all ? all[key] : fallback;
    },
  };
}
